import {
  ArrowRightOutlined,
  CalendarOutlined,
  DollarOutlined,
  FileTextOutlined,
  RiseOutlined
} from "@ant-design/icons"
import { Button, Col, Flex, Row, Typography } from "antd"
import React, { type ReactNode } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { RoutesPaths } from "../../../../constants/routes.ts"
import { budgetFormat } from "../../../../utils/number/budget-format.ts"
import { type Lead } from "../../types/lead.types.ts"
import { WorkspaceCard } from "../cards/WorkspaceCard.tsx"
import { WorkspaceCardValue } from "../cards/WorkspaceCardValue.tsx"
import { LeadOverviewField } from "./LeadOverviewField.tsx"
import { leadFinancialSnapshot } from "./lead-overview-financials.ts"
import {
  type OverviewAccent,
  OverviewCardIcon,
  OverviewCardTitle
} from "./OverviewCardIcon.tsx"

const { Text } = Typography

/** The only way into the financial page; nothing else links to it. */
const leadFinancialDetailsPath = (id: string) => RoutesPaths.lead.financial(id)

/** Purely decorative grouping — the value/empty-state logic still lives in `LeadOverviewField`. */
function FinancialStatTile({
  icon,
  accent,
  label,
  value,
  subValue
}: {
  icon: ReactNode
  accent: OverviewAccent
  label: string
  value: string | null
  /** Omit entirely for a tile with no second line; pass `null` for "present but unset". */
  subValue?: string | null
}) {
  return (
    <Flex
      gap={"middle"}
      align={"center"}
      className={"rounded-card border border-border p-4 h-full"}
    >
      <OverviewCardIcon icon={icon} accent={accent} size={40} />
      <Flex vertical gap={2} className={"min-w-0"}>
        <LeadOverviewField label={label} value={value} />
        {subValue !== undefined && (
          <WorkspaceCardValue value={subValue}>
            <Text type={"secondary"} className={"text-xs"}>
              {subValue}
            </Text>
          </WorkspaceCardValue>
        )}
      </Flex>
    </Flex>
  )
}

interface LeadOverviewFinancialCardProps {
  lead: Lead
}

/** Read-only: every figure here is written on the financial screen. */
function LeadOverviewFinancialCard({ lead }: LeadOverviewFinancialCardProps) {
  const { t } = useTranslation()
  const snapshot = leadFinancialSnapshot(lead)

  const amount = (value: number | null) =>
    value == null ? null : budgetFormat(value)

  return (
    <WorkspaceCard
      cardId={"financial"}
      title={
        <OverviewCardTitle icon={<DollarOutlined />} accent={"green"}>
          {t("leadWorkspace.cards.financial.title")}
        </OverviewCardTitle>
      }
      ariaTitle={t("leadWorkspace.cards.financial.title")}
      className={"h-full"}
    >
      <Flex vertical justify={"space-between"} gap={"middle"} className={"h-full"}>
        <Row gutter={[12, 12]}>
          <Col xs={12} md={6}>
            <FinancialStatTile
              icon={<FileTextOutlined />}
              accent={"blue"}
              label={t("leadWorkspace.cards.financial.estimate")}
              value={amount(snapshot.estimate)}
            />
          </Col>
          <Col xs={12} md={6}>
            <FinancialStatTile
              icon={<DollarOutlined />}
              accent={"green"}
              label={t("leadWorkspace.cards.financial.salesPrice")}
              value={amount(snapshot.salesPrice)}
            />
          </Col>
          <Col xs={12} md={6}>
            <FinancialStatTile
              icon={<CalendarOutlined />}
              accent={"orange"}
              label={t("leadWorkspace.cards.financial.plannedPayable")}
              value={amount(snapshot.plannedPayable)}
            />
          </Col>
          <Col xs={12} md={6}>
            <FinancialStatTile
              icon={<RiseOutlined />}
              accent={"indigo"}
              label={t("leadWorkspace.cards.financial.margin")}
              value={amount(snapshot.margin)}
              subValue={
                snapshot.marginPercent == null
                  ? undefined
                  : `${snapshot.marginPercent.toFixed(1)}%`
              }
            />
          </Col>
        </Row>

        <Link to={leadFinancialDetailsPath(lead.id)}>
          <Button size={"small"} type={"link"} icon={<ArrowRightOutlined />}>
            {t("leadWorkspace.cards.financial.open")}
          </Button>
        </Link>
      </Flex>
    </WorkspaceCard>
  )
}

export { leadFinancialDetailsPath, LeadOverviewFinancialCard }
